/*
 * サブシステム名：請求消込
 * 機能名：請求消込入力
 */
import { mapGetters } from 'vuex'

import flowMixin from '@/mixins/flowMixins'
import validators from '@/rahmen/validators'
import filter from '@/rahmen/utils/filters'

import header from '@/ddv2.library/header'
import message from '@/ddv2.library/messages'
import d2fmt from '@/ddv2.library/utils/formatter'

import DdCustomButton from '@/components/DdCustomButton'
import DdTransparentButton from '@/components/DdTransparentButton'

const DdCodeTextBox = () => import('@/components/DdCodeTextBox')
const DdDateRangeBox = () => import('@/components/DdDateRangeBox')
const DdEditDialog = () => import('@/components/DdEditDialog')
const DdNumericBox = () => import('@/components/DdNumericBox')
const DdSelectItemsPerPage = () => import('@/components/DdSelectItemsPerPage')
const DdTextBox = () => import('@/components/DdTextBox')

export default {
  name: 'SeikyuKeshikomiEdit',
  mixins: [flowMixin],
  components: {
    DdCustomButton,
    DdTransparentButton,
    DdCodeTextBox,
    DdDateRangeBox,
    DdEditDialog,
    DdNumericBox,
    DdSelectItemsPerPage,
    DdTextBox
  },
  data: () => ({
    // ページネーション用
    itemsPerPage1: 0,
    itemsPerPage2: 0,
    page1: 1,
    page2: 1,

    id: 0,
    param: {},
    conditions: {
      nyukinsakiId: '',
      nyukinYmdFromString: '',
      nyukinYmdToString: '',
      seikyusakiId: '',
      nyukinYoteiYmdFromString: '',
      nyukinYoteiYmdToString: ''
    },
    nyukinKingakuGokei: 0,
    seikyuKingakuGokei: 0,
    sagaku: 0,
    seikyuKeshikomiBiko: '',
    rec: {
      seikyuKeshikomiBiko: '',
      seikyuKeshikomiKihonList: [],
      seikyuList: [],
      trSeikyuKihonInfo: {},
      nyukinList: []
    },
    rows: {
      seikyu: [],
      nyukin: []
    },
    furikomiTesuryoKbn: 0,
    invalidMessages: {},
    headersNyukinsakiList: header.mstTorihikisakiHeaders,
    headersSeikyusakiList: header.mstTorihikisakiHeaders,
    modeOfEdit: 1,
    modeKengen: 2,
    nyuHeaders: [],
    seiHeaders: [],
    expanded: [],
    registerFlg: false
  }),
  computed: {
    ...mapGetters({
      viewTitle: 'system/viewTitle',
      helpUseFlg: 'DDMasters/helpUseFlg',
      torihikisakiNyukinsakiList: 'DDMasters/vwCMstTorihikisakiGetNyukinsaki',
      torihikisakiSeikyusakiList: 'DDMasters/vwCMstTorihikisakiGetSeikyusaki'
    }),
    filteredNyukinsakiList () {
      const target = filter.dateFormatter(new Date())
      return this.torihikisakiNyukinsakiList.map((elem) => {
        elem.tekiyoStartYmdDisp = filter.dateFormatter(elem.tekiyoStartYmd)
        elem.tekiyoEndYmdDisp = filter.dateFormatter(elem.tekiyoEndYmd)
        return elem
      }).filter(function (elem) {
        return elem.tekiyoStartYmdDisp <= target && elem.tekiyoEndYmdDisp >= target
      }).sort((elem1, elem2) => {
        if (elem1.hyojiOrder - elem2.hyojiOrder !== 0) { return elem1.hyojiOrder - elem2.hyojiOrder }
        if (elem1.torihikisakiCd > elem2.torihikisakiCd) { return 1 }
        if (elem1.torihikisakiCd === elem2.torihikisakiCd) { return 0 }
        return -1
      })
    },
    filteredSeikyusakiList () {
      const target = filter.dateFormatter(new Date())
      return this.torihikisakiSeikyusakiList.map((elem) => {
        elem.tekiyoStartYmdDisp = filter.dateFormatter(elem.tekiyoStartYmd)
        elem.tekiyoEndYmdDisp = filter.dateFormatter(elem.tekiyoEndYmd)
        return elem
      }).filter(function (elem) {
        return elem.tekiyoStartYmdDisp <= target && elem.tekiyoEndYmdDisp >= target
      }).sort((elem1, elem2) => {
        if (elem1.hyojiOrder - elem2.hyojiOrder !== 0) { return elem1.hyojiOrder - elem2.hyojiOrder }
        if (elem1.torihikisakiCd > elem2.torihikisakiCd) { return 1 }
        if (elem1.torihikisakiCd === elem2.torihikisakiCd) { return 0 }
        return -1
      })
    },
    /*
     * 計算
     */
    // 今回入金消込額(B)合計
    calcNyukinKingakuGokei () {
      this.nyukinKingakuGokei = this.rows.nyukin.reduce((sum, i) => {
        return sum + this.toNum(i.nyukinKeshikomiKingaku)
      }, 0)
      return this.nyukinKingakuGokei
    },
    // 今回請求消込額(D)合計
    calcSeikyuKingakuGokei () {
      this.seikyuKingakuGokei = this.rows.seikyu.reduce((sum, i) => {
        return sum + this.toNum(i.seikyuKeshikomiKingaku)
      }, 0)
      return this.seikyuKingakuGokei
    },
    // 差額(E)=(D)-(B)
    calcSagaku () {
      this.sagaku = this.calcSeikyuKingakuGokei - this.calcNyukinKingakuGokei
      return this.sagaku
    },
    /*
     * 項目制御
     */
    isDeleteDisable () {
      return this.id === 0
    },
    isShoukaiMode () {
      return this.modeOfEdit === 1
    },
    isDeleteButtonDisplay () {
      if (this.modeKengen === 2 && this.modeOfEdit === 1) {
        return true
      }
      return false
    },
    isAutoWariateButtonDisplay () {
      if (this.modeKengen !== 0 && this.modeOfEdit !== 1) {
        return true
      }
      return false
    },
    isAutoWariateButtonDisabled () {
      if (this.rows.seikyu.length === 0 || this.rows.nyukin.length === 0) {
        return false
      }
      return true
    },
    isSaveButtonDisplay () {
      if (this.modeKengen !== 0 && this.modeOfEdit !== 1) {
        return true
      }
      return false
    },
    isSaveButtonDisabled () {
      if (this.sagaku !== 0) {
        return false
      }
      if (this.nyukinKingakuGokei === 0 && this.seikyuKingakuGokei === 0) {
        return false
      }
      return true
    }
  },
  created () {
    this.id = this.$route.params.id
    if (this.id === '0') {
      this.id = 0
    }
    // 遷移元検索情報
    this.param = this.$route.params.param
  },
  mounted () {
    this.initialize()
  },
  methods: {
    beforeRouteLeaveByMenu: function (next) {
      if (this.registerFlg === false) {
        const opt = {
          nextCallback: next
        }
        this.preventTransition(opt)
      } else {
        // 更新後は画面遷移確認ダイアログを出さない
        next()
      }
    },
    getCloneTargetMethod: function () {
      return {
        rows: this.rows,
        conditions: this.conditions,
        rec: this.rec
      }
    },
    initialize: function () {
      const self = this
      const opt = {
        loadMasters: ['vwcmsttorihikisakigetnyukinsaki', 'vwcmsttorihikisakigetseikyusaki'],
        defineHeaderPromise: () => {
          return new Promise(resolve => {
            self.nyuHeaders = [
              { text: '満額', value: 'chkMangaku', align: 'center', sortable: false },
              { text: '入金年月日', value: 'nyukinYmd', sortable: false },
              { text: '入金額', value: 'nyukingaku', align: 'end', sortable: false },
              { text: '振込手数料', value: 'furikomiTesuryo', align: 'end', sortable: false },
              { text: '調整', value: 'choseiKingaku', align: 'end', sortable: false },
              { text: '相殺', value: 'sosaiKingaku', align: 'end', sortable: false },
              { text: '入金額(A`)', value: 'keshikomiZumiNyukinKingaku', align: 'end', sortable: false },
              { text: '振込手数料', value: 'keshikomiFurikomiTesuryo', align: 'end', sortable: false },
              { text: '調整', value: 'keshikomiZumiChoseiKingaku', align: 'end', sortable: false },
              { text: '相殺', value: 'keshikomiZumiSosaiKingaku', align: 'end', sortable: false },
              { text: '入金未消込額(A)-(A`)', value: 'miKeshikomiZumiNyukinKingaku', align: 'end', sortable: false },
              { text: '今回入金消込額(B)', value: 'nyukinKeshikomiKingaku', align: 'end', sortable: false },
              { text: '入金先', value: 'nyukinsakiNm', sortable: false },
              { text: '入金No', value: 'nyukinNo', sortable: false },
              { text: '担当者', value: 'tantoshaNm', sortable: false },
              { text: '摘要', value: 'tekiyo', sortable: false }
            ]
            self.seiHeaders = [
              { text: '満額', value: 'chkMangaku', width: '50px', align: 'center', sortable: false },
              { text: '明細選択', value: 'meisaiSentaku', width: '90px', align: 'center', sortable: false },
              { text: '入金予定日', value: 'nyukinYoteibi', width: '100px', sortable: false },
              { text: '請求日', value: 'seikyuYmd', width: '100px', sortable: false },
              { text: '請求No', value: 'seikyuNo', width: '120px', sortable: false },
              { text: '明細No', value: 'dispSeikyuMeisaiNo', width: '80px', sortable: false },
              { text: '請求先', value: 'seikyusakiNm', width: '100px', sortable: false },
              { text: '得意先', value: 'tokuisakiNm', width: '100px', sortable: false },
              { text: '請求額(C)', value: 'seikyuKingaku', width: '100px', align: 'end', sortable: false },
              { text: '消込済金額(C`)', value: 'keshikomiZumiSeikyuKingaku', width: '100px', align: 'end', sortable: false },
              { text: '請求未消込額(C)-(C`)', value: 'miKeshikomiSeikyuKingaku', width: '100px', align: 'end', sortable: false },
              { text: '今回請求消込額(D)', value: 'seikyuKeshikomiKingaku', width: '100px', align: 'end', sortable: false }
            ]
            resolve()
          })
        },
        initializeInputPromise: function () {
          return new Promise((resolve) => {
            self.conditions = {
              nyukinsakiId: '',
              nyukinYmdFromString: '',
              nyukinYmdToString: '',
              seikyusakiId: '',
              nyukinYoteiYmdFromString: '',
              nyukinYoteiYmdToString: ''
            }
            self.rows = {
              seikyu: [],
              nyukin: []
            }
            // 照会モード時は画面起動時に選択した情報を表示
            if (self.id !== 0) {
              self.HttpClient.get(`/api/v1/VwCD2bcld002001/SearchChildrenByReference?id=${self.id}`)
                .then((data) => {
                  // 照会時のメッセージを表示
                  // self.setInvalidSummaryMessage('BCLI0001')
                  // 入金先マスタの振込手数料フラグを取得
                  self.furikomiTesuryoKbn = 0
                  var nyukinsaki = data.payload.mstNyukinsakiInfo
                  if (nyukinsaki !== undefined) {
                    switch (nyukinsaki.furikomiTesuryoFlg) {
                      case true:
                        self.furikomiTesuryoKbn = 1
                        break
                      case false:
                        self.furikomiTesuryoKbn = 2
                        break
                      default:
                        self.furikomiTesuryoKbn = 0
                    }
                  }
                  // 請求情報
                  self.rows.seikyu = data.payload.seikyuList
                  self.rows.seikyu.map((elem) => {
                    // if (elem.miKeshikomiZumiSeikyuKingaku !== elem.seikyuKeshikomiKingaku) {
                    //   elem.chkMangaku = false
                    // } else {
                    //   elem.chkMangaku = true
                    // }
                    elem.chkMangaku = false
                    elem.nyukinYoteiYmd = filter.dateFormatter(elem.nyukinYoteiYmd)
                    elem.seikyuYmd = filter.dateFormatter(elem.seikyuYmd)
                    elem.konkaiSeikyuKingaku = filter.numericFormatter(Number(elem.konkaiSeikyuKingaku))
                    elem.keshikomiZumiSeikyuKingaku = filter.numericFormatter(Number(elem.keshikomiZumiSeikyuKingaku))
                    elem.miKeshikomiZumiSeikyuKingaku = filter.numericFormatter(Number(elem.miKeshikomiZumiSeikyuKingaku))
                    elem.seikyuKeshikomiKingaku = filter.numericFormatter(Number(elem.seikyuKeshikomiKingaku))
                    // 編集ダイアログ部品を再レンダリングするためのフラグ
                    elem.seikyuKeshikomiKingakuHenkoFlg = false
                    return elem
                  })
                  // 入金情報
                  self.rows.nyukin = data.payload.nyukinList
                  self.rows.nyukin.map((elem) => {
                    // if (elem.miKeshikomiZumiNyukinKingaku !== elem.nyukinKeshikomiKingaku) {
                    //   elem.chkMangaku = false
                    // } else {
                    //   elem.chkMangaku = true
                    // }
                    elem.chkMangaku = false
                    elem.nyukinYmd = filter.dateFormatter(elem.nyukinYmd)
                    elem.miKeshikomiZumiNyukinKingaku = filter.numericFormatter(Number(elem.miKeshikomiZumiNyukinKingaku))
                    elem.keshikomiZumiNyukinKingaku = filter.numericFormatter(Number(elem.keshikomiZumiNyukinKingaku))
                    elem.nyukingaku = filter.numericFormatter(Number(elem.nyukingaku))
                    elem.furikomiTesuryo = filter.numericFormatter(Number(elem.furikomiTesuryo))
                    elem.choseiKingaku = filter.numericFormatter(Number(elem.choseiKingaku))
                    elem.sosaiKingaku = filter.numericFormatter(Number(elem.sosaiKingaku))
                    elem.nyukinKeshikomiKingaku = filter.numericFormatter(Number(elem.nyukinKeshikomiKingaku))
                    // 編集ダイアログ部品を再レンダリングするためのフラグ
                    elem.nyukinKeshikomiKingakuHenkoFlg = false
                    return elem
                  })
                  // 請求消込基本
                  self.rec.seikyuKeshikomiKihonList = data.payload.seikyuKeshikomiKihonList
                  // 請求書に出力済みデータのときは、対象の請求情報を取得
                  self.rec.trSeikyuKihonInfo = data.payload.trSeikyuKihonInfo
                  // 同一消込番号のデータが複数件とれる可能性があるため、1件目のみを設定
                  self.rec.seikyuKeshikomiBiko = self.rec.seikyuKeshikomiKihonList[0].seikyuKeshikomiBiko
                  resolve()
                })
            } else if (self.param !== undefined) {
              // 遷移元から検索条件を渡された場合
              self.conditions.nyukinsakiId = self.param.nyukinsakiId
              self.conditions.nyukinYmdFromString = self.param.nyukinYmdFromString
              self.conditions.nyukinYmdToString = self.param.nyukinYmdToString.length === 0 ? filter.dateFormatter(new Date()) : self.param.nyukinYmdToString
              self.conditions.seikyusakiId = self.param.seikyusakiId
              self.conditions.nyukinYoteiYmdFromString = self.param.nyukinYoteiYmdFromString
              self.conditions.nyukinYoteiYmdToString = self.param.nyukinYoteiYmdToString
              // 検索処理を実行
              // self.onSearchButtonClick()
              self.HttpClient.post('/api/v1/VwCD2bcld002001/SearchChildrenByEdit', self.conditions)
                .then((data) => {
                  self.searchResultMethodFunc(data)
                  // 満額で表示
                  self.onAutoWariateButtonClick()
                  resolve()
                })
            } else {
              resolve()
            }
          })
        },
        savedInputRestorePromise: (c) => {
          console.log('SeikyuKeshikomiEdit.savedInputRestorePromise', c)
          self.conditions = { ...self.conditions, ...c }
          return new Promise(resolve => {
            self.loadFromCache()
              .then((c) => {
                console.log('SeikyuKeshikomiEdit.savedInputRestorePromise', c)
                resolve()
              })
          })
        },
        researchPromise: (c) => {
          console.log('SeikyuKeshikomiEdit.researchPromise', c)
          return new Promise(resolve => {
            self.conditions = c.cache.value.conditions || {}
            self.search().then(() => {
              resolve()
            })
          })
        },
        restoreViewPromise: (c) => {
          console.log('SeikyuKeshikomiEdit.restoreViewPromise', c)
          return new Promise(resolve => {
            self.conditions = c.cache.value.conditions || {}
            self.rows = c.cache.value.rows || {}
            resolve()
          })
        },
        settingButtonStatePromise: (kengen) => {
          console.log('SeikyuKeshikomiEdit.settingButtonStatePromise', kengen)
          return new Promise(resolve => {
            if (kengen.kengenKbn === '10') {
              // 読取モード
              self.modeKengen = 0
            } else if (kengen.kengenKbn === '20') {
              // 削除不可モード
              self.modeKengen = 1
            } else {
              // フルアクセス
              self.modeKengen = 2
            }
            // thisId == 0 then 新規モード
            if (self.id === 0) {
              self.modeOfEdit = 0 // 新規モード
            } else {
              self.modeOfEdit = 1 // 照会モード
            }
            // } else if (this.modeKengen === 0) {
            //   self.modeOfEdit = 1 // 照会モード
            // } else {
            //   self.modeOfEdit = 2 // 更新モード
            // }

            self.$nextTick(() => {
              document.getElementById('heavy-element').style.display = ''
            })

            resolve()
          })
        },
        settingViewScroll: () => {
          return true
        },
        settingLoadEffect: () => {
          // clone値に影響をあたえないために、ここで請求明細の加工を行う
          self.rows.seikyu.map((elem) => {
            // 明細
            const dispSeikyuMeisai = elem.seikyuMeisai.filter(function (elem) {
              return Number(d2fmt.removeComma(elem.seikyuKingaku)) > 0
            })
            dispSeikyuMeisai.map((elem, index) => {
              if (elem.seikyuKeshikomiKingaku !== 0) {
                elem.meisaiSentaku = true
                elem.seikyuKeshikomiKingaku = filter.numericFormatter(Number(d2fmt.removeComma(elem.seikyuKeshikomiKingaku)))
              } else {
                elem.meisaiSentaku = false
                elem.seikyuKeshikomiKingaku = 0
              }
              elem.dispSeikyuMeisaiNo = index + 1
              elem.seikyuKingaku = filter.numericFormatter(Number(d2fmt.removeComma(elem.seikyuKingaku)))
              elem.keshikomiZumiSeikyuKingaku = filter.numericFormatter(Number(d2fmt.removeComma(elem.keshikomiZumiSeikyuKingaku)))
              elem.miKeshikomiZumiSeikyuKingaku = filter.numericFormatter(Number(d2fmt.removeComma(elem.miKeshikomiZumiSeikyuKingaku)))
              // 編集ダイアログ部品を再レンダリングするためのフラグ
              elem.seikyuKeshikomiKingakuHenkoFlg = false
              return elem
            })
            elem.seikyuMeisai = Object.assign([], dispSeikyuMeisai)
            return elem
          })
          return false
        }
      }
      self.initializeForViewer(opt)
    },
    /*
     * ボタン処理_検索
     */
    onSearchButtonClick: function (e) {
      const self = this
      const opt = {
        searchRequestPromise: function () {
          return new Promise(resolve => {
            self.HttpClient.post('/api/v1/VwCD2bcld002001/SearchChildrenByEdit', self.conditions)
              .then((data) => {
                resolve(data)
              })
          })
        },
        searchResultMethod: function (data) {
          self.searchResultMethodFunc(data)
        },
        getSavedInputMethod: function () {
          return {}
        }
      }
      this.search(opt)
    },
    searchValidate: function () {
      var msg
      // 入金日の大小チェック
      if (this.conditions.nyukinYmdFromString !== undefined && this.conditions.nyukinYmdToString !== undefined) {
        msg = validators.compareDate(this.conditions.nyukinYmdFromString, this.conditions.nyukinYmdToString)
        if (msg !== undefined) {
          this.$refs.nyukinYmdFromString[this.Ctrls.SET_INVALID_METHOD](msg)
          this.$refs.nyukinYmdToString[this.Ctrls.SET_INVALID_METHOD](msg)
          this.setInvalidSummaryMessage('COME0014')
          return false
        }
      }
      // 入金予定日の大小チェック
      if (this.conditions.nyukinYoteiYmdFromString !== undefined && this.conditions.nyukinYoteiYmdToString !== undefined) {
        msg = validators.compareDate(this.conditions.nyukinYoteiYmdFromString, this.conditions.nyukinYoteiYmdToString)
        if (msg !== undefined) {
          this.$refs.nyukinYoteiYmdFromString[this.Ctrls.SET_INVALID_METHOD](msg)
          this.$refs.nyukinYoteiYmdToString[this.Ctrls.SET_INVALID_METHOD](msg)
          this.setInvalidSummaryMessage('COME0014')
          return false
        }
      }
      return true
    },
    // 別処理でも使用するため外出し
    searchResultMethodFunc: function (data) {
      // 入金先マスタの振込手数料フラグを取得
      var nyukinsaki = data.payload.mstNyukinsakiInfo
      if (nyukinsaki !== undefined) {
        switch (nyukinsaki.furikomiTesuryoFlg) {
          case true:
            // 出力：振込手数料は当方負担です
            this.furikomiTesuryoKbn = 1
            break
          case false:
            // 出力：振込手数料は先方負担です
            this.furikomiTesuryoKbn = 2
            break
          default:
            // なし
            this.furikomiTesuryoKbn = 0
        }
      }
      // 請求情報
      this.rows.seikyu = data.payload.seikyuList
      this.rows.seikyu.map((elem) => {
        elem.chkMangaku = false
        elem.nyukinYoteiYmd = filter.dateFormatter(elem.nyukinYoteiYmd)
        elem.seikyuYmd = filter.dateFormatter(elem.seikyuYmd)
        elem.konkaiSeikyuKingaku = filter.numericFormatter(Number(elem.konkaiSeikyuKingaku))
        elem.keshikomiZumiSeikyuKingaku = filter.numericFormatter(Number(elem.keshikomiZumiSeikyuKingaku))
        elem.miKeshikomiZumiSeikyuKingaku = filter.numericFormatter(Number(elem.miKeshikomiZumiSeikyuKingaku))
        elem.seikyuKeshikomiKingaku = 0
        // 編集ダイアログ部品を再レンダリングするためのフラグ
        elem.seikyuKeshikomiKingakuHenkoFlg = false
        // 明細
        elem.seikyuMeisai.map((elem, index) => {
          elem.meisaiSentaku = false
          elem.dispSeikyuMeisaiNo = index + 1
          elem.seikyuKingaku = filter.numericFormatter(Number(elem.seikyuKingaku))
          elem.keshikomiZumiSeikyuKingaku = filter.numericFormatter(Number(elem.keshikomiZumiSeikyuKingaku))
          elem.miKeshikomiZumiSeikyuKingaku = filter.numericFormatter(Number(elem.miKeshikomiZumiSeikyuKingaku))
          elem.seikyuKeshikomiKingaku = 0
          // 編集ダイアログ部品を再レンダリングするためのフラグ
          elem.seikyuKeshikomiKingakuHenkoFlg = false
          return elem
        })
        return elem
      })
      // 入金情報
      this.rows.nyukin = data.payload.nyukinList
      this.rows.nyukin.map((elem) => {
        elem.chkMangaku = false
        elem.nyukinYmd = filter.dateFormatter(elem.nyukinYmd)
        elem.miKeshikomiZumiNyukinKingaku = filter.numericFormatter(Number(elem.miKeshikomiZumiNyukinKingaku))
        elem.keshikomiZumiNyukinKingaku = filter.numericFormatter(Number(elem.keshikomiZumiNyukinKingaku))
        elem.nyukingaku = filter.numericFormatter(Number(elem.nyukingaku))
        elem.furikomiTesuryo = filter.numericFormatter(Number(elem.furikomiTesuryo))
        elem.choseiKingaku = filter.numericFormatter(Number(elem.choseiKingaku))
        elem.sosaiKingaku = filter.numericFormatter(Number(elem.sosaiKingaku))
        elem.keshikomiFurikomiTesuryo = filter.numericFormatter(Number(elem.keshikomiFurikomiTesuryo))
        elem.keshikomiZumiChoseiKingaku = filter.numericFormatter(Number(elem.keshikomiZumiChoseiKingaku))
        elem.keshikomiZumiSosaiKingaku = filter.numericFormatter(Number(elem.keshikomiZumiSosaiKingaku))
        elem.nyukinKeshikomiKingaku = 0
        // 編集ダイアログ部品を再レンダリングするためのフラグ
        elem.nyukinKeshikomiKingakuHenkoFlg = false
        return elem
      })

      const cache = {
        rows: this.rows,
        conditions: this.conditions
        // searchText: this.searchText
      }
      this.saveToChache(cache)
        .then(() => {
        })
    },
    /*
     * ボタン処理_クリア
     */
    onClearButtonClick: function (e) {
      this.Effects.loadStart()
      this.clear()
    },
    clear: function () {
      const opt = {
        clearInputsPromise: () => {
          return new Promise(resolve => {
            this.conditions = {
              nyukinsakiId: '',
              nyukinYmdFromString: '',
              nyukinYmdToString: '',
              seikyusakiId: '',
              nyukinYoteiYmdFromString: '',
              nyukinYoteiYmdToString: ''
            }
            this.rows = {
              seikyu: [],
              nyukin: []
            }
            resolve()
          })
        }
      }

      this.clearInputs(opt)
    },
    /*
     * ボタン処理_DdFooterButton
     */
    onReturnButtonClick: function (e) {
      const self = this
      const opt = {
        getTransitionBackParams: function () {
          return {
            actionId: 0,
            param: { id: self.id }
          }
        }
      }

      this.transitionBack(opt)
    },
    onDeleteButtonClick: function (e) {
      const self = this
      const opt = {
        deleteRequestPromise: function () {
          return new Promise(resolve => {
            self.rec = {
              seikyuList: self.cloneData.rows.seikyu,
              nyukinList: self.cloneData.rows.nyukin,
              seikyuKeshikomiKihonList: self.cloneData.rec.seikyuKeshikomiKihonList
            }
            self.HttpClient.post(`/api/v1/VwCD2bcld002001/DeleteMulti?id=${self.id}`, self.rec).then(data => {
              resolve(data)
            })
          })
        },
        getTransitionBackParams: function () {
          // 更新処理後は画面遷移確認ダイアログを出さないためのフラグ
          self.registerFlg = true
          return {
            actionId: 0,
            param: { id: self.id }
          }
        }
      }
      this.delete(opt)
    },
    onAutoWariateButtonClick: function (e) {
      return new Promise(resolve => {
        // 入金情報を全て満額で選択
        this.rows.nyukin.forEach((elem) => {
          this.checkMangakuNyu(true, elem)
        })
        // 請求情報を全て満額で選択
        this.rows.seikyu.forEach((elem) => {
          this.checkMangakuSei(true, elem)
        })
        if (this.calcSagaku !== 0) {
          // 入金情報と請求情報が完全に一致しない場合
          this.setInvalidSummaryMessage('BCLE0001')
        }
        resolve()
      })
    },
    onSaveButtonClick: function (e) {
      const self = this
      const opt = {
        registerRequestPromise: self.registerPromise,
        registerResponsePromise: (data) => {
          return new Promise(resolve => {
            self.id = data.payload
            // 新規作成のみ
            // self.id = 0
            resolve()
          })
        },
        getIdMethod: () => {
          return self.id
        },
        continuousInputInitializePromise: (id) => {
          return new Promise(resolve => {
            // self.id = id
            // self.transition(1, { id: id }, self.initialize)
            // 新規作成のみ
            // self.id = 0
            self.transition(1, { id: self.id }, self.initialize)
            resolve()
          })
        },
        getTransitionBackParams: function () {
          // 更新処理後は画面遷移確認ダイアログを出さないためのフラグ
          self.registerFlg = true
          return {
            actionId: 0,
            param: { id: self.id }
          }
        },
        isUpdateModeMethod: function () {
          return self.id !== 0
        },
        getSavedInputMethod: function () {
          return {}
        }
      }

      this.register(opt)
    },
    registerPromise: function () {
      console.log(this.rows, self.rows)
      let f
      if (this.id === 0) {
        // 請求情報
        this.rec.seikyuList = this.rows.seikyu
        // 入金情報
        this.rec.nyukinList = this.rows.nyukin

        f = this.HttpClient.post('/api/v1/VwCD2bcld002001/CreateMulti', this.rec)
      }
      return f
    },
    registerValidate: function () {
      // ボタンで制御を行っているが念のため
      if (this.calcSagaku !== 0) {
        this.setInvalidSummaryMessage('BCLE0001')
        return false
      }
      // 仕分金額未満の入金消込は不可
      var errorNyukin = this.rows.nyukin.filter((elem) => {
        return (this.toNum(elem.nyukinKeshikomiKingaku) > 0) && (this.toNum(elem.nyukinKeshikomiKingaku) < (this.toNum(elem.furikomiTesuryo) + this.toNum(elem.choseiKingaku) + this.toNum(elem.sosaiKingaku)))
      })
      if (errorNyukin.length !== 0) {
        this.setInvalidSummaryMessage('BCLE0002')
        return false
      }

      return true
    },
    deleteValidate: function () {
      console.log(this.rec)
      var seikyuKeshikomiKihon = this.rec.seikyuKeshikomiKihonList.filter((t) => {
        return t.trSeikyuKihonId > 0
      })
      if (seikyuKeshikomiKihon.length > 0) {
        var seikyuYm = this.rec.trSeikyuKihonInfo.seikyuYm
        var seikyuYear = seikyuYm.substr(0, seikyuYm.indexOf('/'))
        var seikyuMonth = seikyuYm.substr(seikyuYm.indexOf('/') + 1)
        var msgInfo = JSON.parse(JSON.stringify(message.getInfo('BCLE0004')))
        msgInfo.message = message.get('BCLE0004', [seikyuYear, seikyuMonth, this.rec.trSeikyuKihonInfo.seikyuNo])
        this.setSummaryMessage(msgInfo)
        return false
      }
      return true
    },
    /* ******************
     * 表項目
     *   請求情報
     ****************** */
    /*
     * 表項目_満額チェックボックス
     */
    checkMangakuSei: function (e, row) {
      // 更新対象のインデックスを取得
      var targetIdx = this.rows.seikyu.findIndex(elem => elem.id === row.id)

      if (e === true) {
        // 満額フラグONの場合
        // 今回請求消込額に消込可能金額の上限を設定
        this.rows.seikyu[targetIdx].chkMangaku = true
        this.rows.seikyu[targetIdx].seikyuKeshikomiKingaku = this.rows.seikyu[targetIdx].miKeshikomiZumiSeikyuKingaku
        this.rows.seikyu[targetIdx].seikyuKeshikomiKingakuHenkoFlg = !this.rows.seikyu[targetIdx].seikyuKeshikomiKingakuHenkoFlg
        // 金額分配処理
        this.bunpaiKingakuSeikyuKihonToSeikyuMeisai(targetIdx, this.toNum(this.rows.seikyu[targetIdx].seikyuKeshikomiKingaku))
      } else {
        // 今回請求消込額を0にする
        this.rows.seikyu[targetIdx].chkMangaku = false
        this.rows.seikyu[targetIdx].seikyuKeshikomiKingaku = 0
        this.rows.seikyu[targetIdx].seikyuKeshikomiKingakuHenkoFlg = !this.rows.seikyu[targetIdx].seikyuKeshikomiKingakuHenkoFlg
        // 金額分配処理
        this.bunpaiKingakuSeikyuKihonToSeikyuMeisai(targetIdx, this.toNum(this.rows.seikyu[targetIdx].seikyuKeshikomiKingaku))
      }
      // DOM更新の発火
      this.rows.seikyu.splice()
    },
    /*
     * 表項目_DdEditDialog
     */
    // 今回請求消込額（基本）
    saveSeikyuKeshikomiKingakuKihon: function (e, row) {
      // 更新対象のインデックスを取得
      var targetIdxKihon = this.rows.seikyu.findIndex(elem => elem.id === row.id)

      if (this.toNum(row.miKeshikomiZumiSeikyuKingaku) < this.toNum(e)) {
        // 今回請求消込額に消込可能金額を超える金額を入力した場合
        // 更新前の値に更新し直す
        this.rows.seikyu[targetIdxKihon].seikyuKeshikomiKingaku = row.seikyuKeshikomiKingaku
        this.rows.seikyu[targetIdxKihon].seikyuKeshikomiKingakuHenkoFlg = !this.rows.seikyu[targetIdxKihon].seikyuKeshikomiKingakuHenkoFlg
      } else {
        // 更新後の値に更新する
        this.rows.seikyu[targetIdxKihon].seikyuKeshikomiKingaku = filter.numericFormatter(e)
        this.rows.seikyu[targetIdxKihon].seikyuKeshikomiKingakuHenkoFlg = !this.rows.seikyu[targetIdxKihon].seikyuKeshikomiKingakuHenkoFlg
        if (this.toNum(e) === this.toNum(row.miKeshikomiZumiSeikyuKingaku)) {
          // 今回請求消込額に消込可能金額の上限を入力した場合
          this.rows.seikyu[targetIdxKihon].chkMangaku = true
        } else {
          this.rows.seikyu[targetIdxKihon].chkMangaku = false
        }
        // 金額分配処理
        this.bunpaiKingakuSeikyuKihonToSeikyuMeisai(targetIdxKihon, this.toNum(e))
      }
      // DOM更新の発火
      this.rows.seikyu.splice()
    },
    // 今回請求消込額（明細）
    saveSeikyuKeshikomiKingakuMeisai: function (e, row) {
      // 更新対象のインデックスを取得
      var targetIdxKihon = this.rows.seikyu.findIndex(elem => elem.id === row.trSeikyuKihonId)
      var targetIdcMeisai = this.rows.seikyu[targetIdxKihon].seikyuMeisai.findIndex((elem) => elem.trSeikyuTokuisakibetsuId === row.trSeikyuTokuisakibetsuId && elem.trSeikyuMeisaiId === row.trSeikyuMeisaiId)

      if (this.toNum(row.miKeshikomiZumiSeikyuKingaku) < this.toNum(e)) {
        // 今回請求消込額に消込可能金額を超える金額を入力した場合
        // 更新前の値に更新し直す
        this.rows.seikyu[targetIdxKihon].seikyuMeisai[targetIdcMeisai].seikyuKeshikomiKingaku = row.seikyuKeshikomiKingaku
        this.rows.seikyu[targetIdxKihon].seikyuMeisai[targetIdcMeisai].seikyuKeshikomiKingakuHenkoFlg = !this.rows.seikyu[targetIdxKihon].seikyuMeisai[targetIdcMeisai].seikyuKeshikomiKingakuHenkoFlg
      } else {
        // 更新後の値に更新する
        this.rows.seikyu[targetIdxKihon].seikyuMeisai[targetIdcMeisai].seikyuKeshikomiKingaku = filter.numericFormatter(e)
        this.rows.seikyu[targetIdxKihon].seikyuMeisai[targetIdcMeisai].seikyuKeshikomiKingakuHenkoFlg = !this.rows.seikyu[targetIdxKihon].seikyuMeisai[targetIdcMeisai].seikyuKeshikomiKingakuHenkoFlg
        if (this.toNum(e) === 0) {
          // 今回請求消込額に0を入力した場合
          this.rows.seikyu[targetIdxKihon].seikyuMeisai[targetIdcMeisai].meisaiSentaku = false
        } else {
          this.rows.seikyu[targetIdxKihon].seikyuMeisai[targetIdcMeisai].meisaiSentaku = true
        }
        // 金額集計処理
        this.shukeiKingakuSeikyuMeisaiToSeikyuKihon(targetIdxKihon)
      }
      // DOM更新の発火
      this.rows.seikyu.splice()
    },
    /* ******************
     * 表項目
     *   入金情報
     ****************** */
    /*
     * 表項目_満額チェックボックス
     */
    checkMangakuNyu: function (e, row) {
      // 更新対象のインデックスを取得
      var targetIdx = this.rows.nyukin.findIndex(elem => elem.id === row.id)

      if (e === true) {
        // 満額フラグONの場合
        // 今回入金消込額に消込可能金額の上限を設定
        this.rows.nyukin[targetIdx].chkMangaku = true
        this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku = this.rows.nyukin[targetIdx].miKeshikomiZumiNyukinKingaku
        this.rows.nyukin[targetIdx].nyukinKeshikomiKingakuHenkoFlg = !this.rows.nyukin[targetIdx].nyukinKeshikomiKingakuHenkoFlg
      } else {
        // 今回請求消込額を0にする
        this.rows.nyukin[targetIdx].chkMangaku = false
        this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku = 0
        this.rows.nyukin[targetIdx].nyukinKeshikomiKingakuHenkoFlg = !this.rows.nyukin[targetIdx].nyukinKeshikomiKingakuHenkoFlg
      }
      // DOM更新の発火
      this.rows.nyukin.splice()
    },
    /*
     * 表項目_DdEditDialog
     */
    // 振込手数料
    saveFurikomiTesuryo: function (e, row) {
      // 更新対象のインデックスを取得
      var targetIdx = this.rows.nyukin.findIndex(elem => elem.id === row.id)

      // 更新後の値に更新する
      this.rows.nyukin[targetIdx].furikomiTesuryo = filter.numericFormatter(e)
      var miKeshikomiNyukinKingaku = this.toNum(row.nyukingaku) - this.toNum(row.keshikomiZumiNyukinKingaku) + this.toNum(e) + this.toNum(row.choseiKingaku) + this.toNum(row.sosaiKingaku)
      this.rows.nyukin[targetIdx].miKeshikomiZumiNyukinKingaku = filter.numericFormatter(miKeshikomiNyukinKingaku)

      if (this.toNum(this.rows.nyukin[targetIdx].miKeshikomiZumiNyukinKingaku) === this.toNum(this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku)) {
        // 今回入金消込額に消込可能金額の上限を入力した場合
        this.rows.nyukin[targetIdx].chkMangaku = true
      } else if (this.toNum(this.rows.nyukin[targetIdx].miKeshikomiZumiNyukinKingaku) < this.toNum(this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku)) {
        // 今回入金消込額が消込可能金額を超えた場合
        this.rows.nyukin[targetIdx].chkMangaku = false
        this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku = 0
      } else {
        this.rows.nyukin[targetIdx].chkMangaku = false
      }
      // DOM更新の発火
      this.rows.nyukin.splice()
    },
    // 調整
    saveChoseiKingaku: function (e, row) {
      // 更新対象のインデックスを取得
      var targetIdx = this.rows.nyukin.findIndex(elem => elem.id === row.id)

      // 更新後の値に更新する
      this.rows.nyukin[targetIdx].choseiKingaku = filter.numericFormatter(e)
      var miKeshikomiNyukinKingaku = this.toNum(row.nyukingaku) - this.toNum(row.keshikomiZumiNyukinKingaku) + this.toNum(row.furikomiTesuryo) + this.toNum(e) + this.toNum(row.sosaiKingaku)
      this.rows.nyukin[targetIdx].miKeshikomiZumiNyukinKingaku = filter.numericFormatter(miKeshikomiNyukinKingaku)

      if (this.toNum(this.rows.nyukin[targetIdx].miKeshikomiZumiNyukinKingaku) === this.toNum(this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku)) {
        // 今回入金消込額に消込可能金額の上限を入力した場合
        this.rows.nyukin[targetIdx].chkMangaku = true
      } else if (this.toNum(this.rows.nyukin[targetIdx].miKeshikomiZumiNyukinKingaku) < this.toNum(this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku)) {
        // 今回入金消込額が消込可能金額を超えた場合
        this.rows.nyukin[targetIdx].chkMangaku = false
        this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku = 0
      } else {
        this.rows.nyukin[targetIdx].chkMangaku = false
      }
      // DOM更新の発火
      this.rows.nyukin.splice()
    },
    // 相殺
    saveSosaiKingaku: function (e, row) {
      // 更新対象のインデックスを取得
      var targetIdx = this.rows.nyukin.findIndex(elem => elem.id === row.id)

      // 更新後の値に更新する
      this.rows.nyukin[targetIdx].sosaiKingaku = filter.numericFormatter(e)
      var miKeshikomiNyukinKingaku = this.toNum(row.nyukingaku) - this.toNum(row.keshikomiZumiNyukinKingaku) + this.toNum(row.furikomiTesuryo) + this.toNum(row.choseiKingaku) + this.toNum(e)
      this.rows.nyukin[targetIdx].miKeshikomiZumiNyukinKingaku = filter.numericFormatter(miKeshikomiNyukinKingaku)

      if (this.toNum(this.rows.nyukin[targetIdx].miKeshikomiZumiNyukinKingaku) === this.toNum(this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku)) {
        // 今回入金消込額に消込可能金額の上限を入力した場合
        this.rows.nyukin[targetIdx].chkMangaku = true
      } else if (this.toNum(this.rows.nyukin[targetIdx].miKeshikomiZumiNyukinKingaku) < this.toNum(this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku)) {
        // 今回入金消込額が消込可能金額を超えた場合
        this.rows.nyukin[targetIdx].chkMangaku = false
        this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku = 0
      } else {
        this.rows.nyukin[targetIdx].chkMangaku = false
      }
      // DOM更新の発火
      this.rows.nyukin.splice()
    },
    // 消込入金額
    saveNyukinKeshikomiKingaku: function (e, row) {
      // 更新対象のインデックスを取得
      var targetIdx = this.rows.nyukin.findIndex(elem => elem.id === row.id)

      if (this.toNum(row.miKeshikomiZumiNyukinKingaku) < this.toNum(e)) {
        // 今回入金消込額に消込可能金額を超える金額を入力した場合
        // 更新前の値に更新し直す
        this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku = row.nyukinKeshikomiKingaku
        this.rows.nyukin[targetIdx].nyukinKeshikomiKingakuHenkoFlg = !this.rows.nyukin[targetIdx].nyukinKeshikomiKingakuHenkoFlg
      } else {
        // 更新後の値に更新する
        this.rows.nyukin[targetIdx].nyukinKeshikomiKingaku = filter.numericFormatter(e)
        this.rows.nyukin[targetIdx].nyukinKeshikomiKingakuHenkoFlg = !this.rows.nyukin[targetIdx].nyukinKeshikomiKingakuHenkoFlg
        if (this.toNum(e) === this.toNum(row.miKeshikomiZumiNyukinKingaku)) {
          // 今回入金消込額に消込可能金額の上限を入力した場合
          this.rows.nyukin[targetIdx].chkMangaku = true
        } else {
          this.rows.nyukin[targetIdx].chkMangaku = false
        }
      }
      // DOM更新の発火
      this.rows.nyukin.splice()
    },
    /*
     * 金額分配処理_基本から明細へ
     */
    bunpaiKingakuSeikyuKihonToSeikyuMeisai: function (targetIdx, kihonKingaku) {
      var totalKingaku = kihonKingaku
      return new Promise(resolve => {
        this.rows.seikyu[targetIdx].seikyuMeisai = this.rows.seikyu[targetIdx].seikyuMeisai.map((elem) => {
          if (kihonKingaku !== 0) {
            if ((totalKingaku - this.toNum(elem.miKeshikomiZumiSeikyuKingaku)) >= 0) {
              // 分配中の基本金額内で明細に対する満額消込が可能な場合
              elem.seikyuKeshikomiKingaku = elem.miKeshikomiZumiSeikyuKingaku
              elem.meisaiSentaku = true
            } else if ((totalKingaku - this.toNum(elem.miKeshikomiZumiSeikyuKingaku)) > (this.toNum(elem.miKeshikomiZumiSeikyuKingaku) * -1)) {
              // 分配中の基本金額内で明細に対する消込が一部可能な場合
              elem.seikyuKeshikomiKingaku = filter.numericFormatter(this.toNum(totalKingaku))
              elem.meisaiSentaku = true
            } else {
              // 分配中の基本金額内で明細に対する消込が一部でも不可能な場合
              elem.seikyuKeshikomiKingaku = 0
              elem.meisaiSentaku = false
            }
            // 基本金額から明細に分配した金額を減算
            totalKingaku = totalKingaku - this.toNum(elem.miKeshikomiZumiSeikyuKingaku)
          } else {
            elem.seikyuKeshikomiKingaku = 0
            elem.meisaiSentaku = false
          }
          elem.seikyuKeshikomiKingakuHenkoFlg = !elem.seikyuKeshikomiKingakuHenkoFlg
          return elem
        })
        resolve()
      })
    },
    /*
     * 金額集計処理_明細を基本に
     */
    shukeiKingakuSeikyuMeisaiToSeikyuKihon: function (targetIdx) {
      return new Promise(resolve => {
        var totalKingaku = this.rows.seikyu[targetIdx].seikyuMeisai.reduce((sum, i) => {
          var value = this.toNum(i.seikyuKeshikomiKingaku)
          return sum + value
        }, 0)
        this.rows.seikyu[targetIdx].seikyuKeshikomiKingaku = filter.numericFormatter(totalKingaku)
        this.rows.seikyu[targetIdx].seikyuKeshikomiKingakuHenkoFlg = !this.rows.seikyu[targetIdx].seikyuKeshikomiKingakuHenkoFlg
        if (this.rows.seikyu[targetIdx].seikyuKeshikomiKingaku === this.rows.seikyu[targetIdx].miKeshikomiZumiSeikyuKingaku) {
          // 集計金額が消込可能金額に達した場合
          this.rows.seikyu[targetIdx].chkMangaku = true
        } else {
          this.rows.seikyu[targetIdx].chkMangaku = false
        }
        resolve()
      })
    },
    /*
     * 数値項目から数値を取得
     */
    toNum: function (val) {
      return Number(filter.numericFormatter(val).replaceAll(',', ''))
    },
    // ------------------------------------------------------------------------------------------
    // ページ操作
    // ------------------------------------------------------------------------------------------
    changeItemsPerPage1: function (e) {
      console.log('changeItemsPerPage', e)
      this.itemsPerPage1 = e
      // 子コンポーネントへの反映
      this.$nextTick().then(() => {
        this.$refs.pagination11.syncPagination(this.itemsPerPage1, this.page1)
        this.$refs.pagination12.syncPagination(this.itemsPerPage1, this.page1)
      })
    },
    changePage1: function (e) {
      this.page1 = e
      // 子コンポーネントへの反映
      this.$nextTick().then(() => {
        this.$refs.pagination11.syncPagination(this.itemsPerPage1, this.page1)
        this.$refs.pagination12.syncPagination(this.itemsPerPage1, this.page1)
      })
    },
    changeItemsPerPage2: function (e) {
      console.log('changeItemsPerPage', e)
      this.itemsPerPage2 = e
      // 子コンポーネントへの反映
      this.$nextTick().then(() => {
        this.$refs.pagination21.syncPagination(this.itemsPerPage2, this.page2)
        this.$refs.pagination22.syncPagination(this.itemsPerPage2, this.page2)
      })
    },
    changePage2: function (e) {
      this.page2 = e
      // 子コンポーネントへの反映
      this.$nextTick().then(() => {
        this.$refs.pagination21.syncPagination(this.itemsPerPage2, this.page2)
        this.$refs.pagination22.syncPagination(this.itemsPerPage2, this.page2)
      })
    },
    // ダミー
    onDummyFunction: function () {
      this.$emit('dummy')
    }
  }
}
